import { PortableTextProps } from '@portabletext/react'
import { cn, Media, OrContentBlockD, OrContentBlockE, OrContentBlockF, OrContentBlockG } from '@studio/atomic-ui'

import { SanityAtImage, SanitySvgAsset } from '../../services/sanity/types'
import { mapImage } from '../../services/sanity/client'
import { ObserverRefsType } from '../../types/observerRef'

export interface SanityAssetList {
  items: Array<{ icon: SanitySvgAsset; title: string; body?: string }>
}

type ContentBlockOrientation = 'normal' | 'reverse'
export interface ContentBlock {
  id: string
  layout: string
  sectionTitle: string
  sectionDescription?: string
  sectionBody: PortableTextProps['value']
  images: Array<SanityAtImage>
  media: Array<Media>
  list?: SanityAssetList
  orientation?: ContentBlockOrientation
}

interface ContentBlockProps {
  content: ContentBlock
  observerRefs: ObserverRefsType
}

export const ContentBlock = ({ content, observerRefs }: ContentBlockProps) => {
  switch (content.layout) {
    case 'longText':
      return (
        <div
          className="mt-12 pl-4 lg:px-0 lg:pl-[10.438rem] scroll-mt-24"
          id={content.id}
          ref={(el) => {
            observerRefs.current[content.id] = el
          }}
        >
          <OrContentBlockD
            title={content.sectionTitle}
            content={content.sectionBody}
            media={
              content?.media
                ? content.media.map((m) => mapImage(m, { width: 806 }))
                : content?.images.map((image) => mapImage(image, { width: 806 }))
            }
          />
        </div>
      )

    case 'featureList':
      return (
        <div
          className="mt-12 scroll-mt-24"
          id={content.id}
          ref={(el) => {
            observerRefs.current[content.id] = el
          }}
        >
          <OrContentBlockE
            title={content.sectionTitle}
            subTitle={content.sectionDescription}
            informative={content.list?.items.map((item) => ({
              title: item.title,
              icon: item.icon?.src,
              label: item.body,
            }))}
            media={
              content?.media
                ? content.media.map((m) => mapImage(m, { width: 468 }))
                : content?.images.map((image) => mapImage(image, { width: 468 }))
            }
          />
        </div>
      )

    case 'paragraphBody':
      return (
        <div
          className={cn('mt-30 scroll-mt-24 lg:mx-auto lg:max-w-screen-lg')}
          id={content.id}
          ref={(el) => {
            observerRefs.current[content.id] = el
          }}
        >
          <OrContentBlockF
            title={content.sectionTitle}
            mediaOne={
              content?.media ? mapImage(content.media[0], { width: 710 }) : mapImage(content?.images[0], { width: 710 })
            }
            mediaTwo={
              content?.media ? mapImage(content.media[1], { width: 710 }) : mapImage(content?.images[1], { width: 710 })
            }
            body={content.sectionBody}
            orientation={content?.orientation}
          />
        </div>
      )

    case 'descriptions':
      return (
        <div
          className="mt-32 px-4 lg:mt-32 scroll-mt-24"
          id={content.id}
          ref={(el) => {
            observerRefs.current[content.id] = el
          }}
        >
          <OrContentBlockG
            title={content.sectionTitle}
            subTitle={content.sectionDescription}
            media={
              content?.media
                ? content.media.map((m) => mapImage(m, { width: 1106 }))
                : content?.images.map((image) => mapImage(image, { width: 1106 }))
            }
            informative={content.list?.items.map((item) => ({
              title: item.title,
              icon: item.icon?.src,
              label: item.body,
            }))}
          />
        </div>
      )

    default:
      return null
  }
}
